import { createFileRoute, Link } from "@tanstack/react-router";
import useAppStore from "@/store";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { InputField } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import ChangeLanguage from "@/components/ui/shared/change-language";
import { useEffect } from "react";
import { Alert } from "@/components/ui/alert";

type ResetPasswordSearch = {
  token?: string;
};

export const Route = createFileRoute("/(onboarding)/auth/reset-password")({
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => {
    return {
      token: (search.token as string) || "",
    };
  },
  component: ResetPassword,
});

function ResetPassword() {
  const store = useAppStore();
  const { t } = useTranslate();

  const { token } = Route.useSearch();

  const form = useSimpleForm({
    password: "",
    token: token,
  });

  useEffect(() => {
    if (token) {
      form.setValues({ ...form.values, token });
    }
  }, [token]);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    store.errors.reset();
    store.loading.start("reset");
    store.auth
      .resetPassword(form.values)
      .then(() => {
        store.display.show("password_updated");
      })
      .catch((error: any) => store.errors.catch(error))
      .finally(() => {
        store.loading.stop("reset");
      });
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="w-1/2 hidden md:block relative overflow-hidden p-4 lg:p-0">
        <div className="absolute top-4 left-4">
          <img
            src="/assets/images/logo.svg"
            alt="SalesBox Logo"
            width={120}
            height={40}
          />
        </div>
        <div className="flex items-center justify-center h-screen">
          <img
            src="/assets/images/reset-password.svg"
            alt="3D Character"
            className="w-3/5 max-h-full"
          />
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-4 sm:p-8 mx-auto bg-white relative">
        <div className="absolute top-4 right-8">
          <ChangeLanguage />
        </div>
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-bold mb-2 tracking-tight text-gray-900">
            {t("Secure Your Account")}
          </h1>
          <p className="text-gray-500 mb-8 leading-relaxed">
            {t(
              "Choose a strong, unique password to protect your account and keep your business data safe."
            )}
          </p>
          <Alert
            name="password_updated"
            message={t("Password updated successfully. You can now login.")}
          />

          {store.display.visible.password_updated ? (
            <>
              <Link
                className="w-full text-white w-full bg-primary-500 hover:bg-primary-600 focus:ring-blue-300 text-sm font-medium  px-5 py-1.5 focus:outline-none focus:ring-4 inline-flex gap-1.5 items-center justify-center rounded-md"
                to="/auth/login"
              >
                {t("Back to Login")}
              </Link>
            </>
          ) : (
            <>
              <form className="space-y-4" onSubmit={handleReset}>
                <div>{store.errors.render()}</div>

                <div>
                  <InputField
                    label={t("New Password")}
                    type="password"
                    name="password"
                    canToggleType={true}
                    value={form.values.password}
                    onChange={form.handleChange}
                  />
                </div>
                <Button
                  className="w-full"
                  type="submit"
                  disabled={store.loading.status.reset || !token}
                  loading={store.loading.status.reset}
                >
                  {t("Update Password")}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500 flex justify-center gap-2">
                  <Link
                    to="/auth/login"
                    className="text-primary-gray hover:underline"
                  >
                    {t("Back to login")}
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
